import { createTheme, type MantineColorsTuple, type CSSVariablesResolver, type MantineThemeOverride } from '@mantine/core';
import { vizAccentVars } from './VizAccents';

const zinc: MantineColorsTuple = ['#fafafa', '#f4f4f5', '#e4e4e7', '#d4d4d8', '#a1a1aa', '#71717a', '#52525b', '#3f3f46', '#27272a', '#18181b'];

/**
 * Brand palette (style guide): Indigo #474F9F (accent), Periwinkle #8B93E0 (highlights),
 * Ice #E8EAFB (tints/text), Deep Navy #0A0C1E (primary surface). Index 6 is the brand accent.
 */
const brand: MantineColorsTuple = [
    '#eef0fc',
    '#e8eafb',
    '#cfd3f3',
    '#b3b9ec',
    '#9aa1e6',
    '#8b93e0',
    '#474f9f',
    '#3f478f',
    '#363d7c',
    '#0a0c1e',
];

/**
 * Dark surfaces built from Deep Navy. Mantine maps dark[7] → body, dark[6] → surface/card/input,
 * dark[5] → hover, dark[4] → border, dark[2] → dimmed text, dark[0] → text.
 */
const navy: MantineColorsTuple = [
    '#e8eafb',
    '#c7ccf0',
    '#9aa1d6',
    '#6b7299',
    '#2b3052',
    '#1c2140',
    '#141833',
    '#0a0c1e',
    '#080a18',
    '#050610',
];

const sharedTheme: MantineThemeOverride = {
    primaryColor: 'brand',
    primaryShade: 6,
    fontFamily: 'Calibri, "Segoe UI", system-ui, -apple-system, sans-serif',
    fontFamilyMonospace: 'JetBrains Mono, monospace',
    colors: {
        zinc,
        brand,
        dark: navy,
    },
    headings: {
        fontFamily: '"Trebuchet MS", "Segoe UI", system-ui, -apple-system, sans-serif',
        fontWeight: '700',
    },
    components: {
        Button: {
            styles: {
                root: {
                    fontWeight: 700,
                },
            },
        },
        NavLink: {
            styles: {
                root: {
                    borderRadius: 'var(--bm-radius, 2px)',
                },
                label: {
                    fontSize: '13px',
                },
            },
        },
        Title: {
            styles: {
                root: {
                    letterSpacing: '-0.02em',
                },
            },
        },
        SegmentedControl: {
            styles: {
                root: {
                    background: 'var(--bm-color-surface-alt)',
                    border: '1px solid var(--bm-color-border)',
                },
                indicator: {
                    background: 'var(--bm-color-card)',
                    boxShadow: 'var(--bm-card-shadow)',
                },
                label: {
                    fontWeight: 600,
                },
            },
        },
    },
};

export const operationsTheme = createTheme({
    ...sharedTheme,
    defaultRadius: 0,
    other: {
        style: 'operations',
        radius: '0px',
        cardShadow: 'none',
        cardShadowHover: '0 8px 24px rgba(0, 0, 0, 0.08)',
        spacing: 'md',
    },
    components: {
        ...sharedTheme.components,
        Paper: {
            defaultProps: {
                shadow: undefined,
            },
            styles: {
                root: {
                    border: '1px solid var(--bm-color-border)',
                },
            },
        },
        Tabs: {
            styles: {
                tab: {
                    fontWeight: 700,
                    fontSize: '11px',
                },
            },
        },
    },
});

export const standardTheme = createTheme({
    ...sharedTheme,
    defaultRadius: 6,
    other: {
        style: 'standard',
        radius: '6px',
        cardShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
        cardShadowHover: '0 12px 32px rgba(0, 0, 0, 0.1)',
        spacing: 'lg',
    },
    components: {
        ...sharedTheme.components,
        Paper: {
            defaultProps: {
                shadow: 'xs',
            },
            styles: {
                root: {
                    border: '1px solid var(--bm-color-border-subtle)',
                },
            },
        },
        Tabs: {
            styles: {
                tab: {
                    fontWeight: 600,
                    fontSize: '12px',
                },
            },
        },
    },
});

export interface ThemeModePalette {
    body: string;
    canvas: string;
    card: string;
    surface: string;
    surfaceAlt: string;
    border: string;
    borderSubtle: string;
    text: string;
    textDimmed: string;
    zincHover: string;
    stripe: string;
    /** Selection-highlight ring — a gold that is deliberately none of the viz accents, so a highlighted item reads distinctly against accent-coloured content. */
    highlight: string;
    /** Soft fill behind a highlighted item. */
    highlightSoft: string;
    /** Brand accent for chrome text/icons/frames — indigo on light, periwinkle on dark (mirrors the brand deck). */
    brandAccent: string;
    /** Soft brand fill — icon tiles, prompt bubbles, selected states. */
    brandSoft: string;
    /** Brand-tinted 1px border to pair with `brandSoft`. */
    brandBorder: string;
    /** Table/grid column-header band (Ice on light, deep navy step on dark). */
    tableHeader: string;
    /** Text/glyph colour on the table-header band. */
    tableHeaderText: string;
    /** Recessed surface for de-emphasised panels (e.g. collapsed drawer sections). */
    surfaceMuted: string;
    /**
     * Categorical brand chart ramp (`--bm-chart-1..8`) for single-hue-family charts
     * (donut slices, prior-month lines, breakdown bars). Ordered for adjacent
     * contrast; the dark ramp avoids near-navy steps that vanish on dark cards.
     */
    chartRamp: [string, string, string, string, string, string, string, string];
    logoFg: string;
    logoAccent: string;
}

export const lightPalette: ThemeModePalette = {
    body: '#ffffff',
    canvas: '#f5f6fc',
    card: '#ffffff',
    surface: '#f5f6fc',
    surfaceAlt: '#eef0fa',
    border: '#e1e3f0',
    borderSubtle: '#eef0fa',
    text: '#0a0c1e',
    textDimmed: '#5e6489',
    zincHover: '#e8eafb',
    stripe: '#eef0fa',
    highlight: '#ca8a04',
    highlightSoft: 'rgba(234, 179, 8, 0.15)',
    brandAccent: '#474f9f',
    brandSoft: 'rgba(71, 79, 159, 0.08)',
    brandBorder: 'rgba(71, 79, 159, 0.25)',
    tableHeader: '#e8eafb',
    tableHeaderText: '#0a0c1e',
    surfaceMuted: '#eef0fa',
    chartRamp: ['#474f9f', '#8b93e0', '#9aa1e6', '#363d7c', '#b3b9ec', '#3f478f', '#cfd3f3', '#0a0c1e'],
    logoFg: '#353535',
    logoAccent: '#474f9f',
};

export const darkPalette: ThemeModePalette = {
    body: '#0a0c1e',
    canvas: '#0a0c1e',
    card: '#141833',
    surface: '#141833',
    surfaceAlt: '#0f1228',
    border: '#2b3052',
    borderSubtle: '#1c2140',
    text: '#e8eafb',
    textDimmed: '#9aa1d6',
    zincHover: '#1c2140',
    stripe: '#1c2140',
    highlight: '#facc15',
    highlightSoft: 'rgba(250, 204, 21, 0.15)',
    brandAccent: '#8b93e0',
    brandSoft: 'rgba(139, 147, 224, 0.12)',
    brandBorder: 'rgba(139, 147, 224, 0.35)',
    tableHeader: '#1c2140',
    tableHeaderText: '#e8eafb',
    surfaceMuted: '#0f1228',
    chartRamp: ['#8b93e0', '#b3b9ec', '#474f9f', '#cfd3f3', '#6b7299', '#9aa1e6', '#e8eafb', '#3f478f'],
    logoFg: '#e8eafb',
    logoAccent: '#8b93e0',
};

function modeVars(p: ThemeModePalette): Record<string, string> {
    return {
        '--bm-color-body': p.body,
        '--bm-color-canvas': p.canvas,
        '--bm-color-card': p.card,
        '--bm-color-surface': p.surface,
        '--bm-color-surface-alt': p.surfaceAlt,
        '--bm-color-border': p.border,
        '--bm-color-border-subtle': p.borderSubtle,
        '--bm-color-text': p.text,
        '--bm-color-text-dimmed': p.textDimmed,
        '--bm-color-zinc-hover': p.zincHover,
        '--bm-color-stripe': p.stripe,
        '--bm-color-highlight': p.highlight,
        '--bm-color-highlight-soft': p.highlightSoft,
        '--bm-color-brand-accent': p.brandAccent,
        '--bm-color-brand-soft': p.brandSoft,
        '--bm-color-brand-border': p.brandBorder,
        '--bm-color-table-header': p.tableHeader,
        '--bm-color-table-header-text': p.tableHeaderText,
        '--bm-color-surface-muted': p.surfaceMuted,
        '--ut-logo-fg': p.logoFg,
        '--ut-logo-accent': p.logoAccent,
        ...Object.fromEntries(p.chartRamp.map((color, index) => [`--bm-chart-${index + 1}`, color])),
    };
}

export const themeVariablesResolver: CSSVariablesResolver = (theme) => ({
    variables: {
        '--bm-radius': theme.other.radius ?? '0px',
        '--bm-card-shadow': theme.other.cardShadow ?? 'none',
        '--bm-card-shadow-hover': theme.other.cardShadowHover ?? '0 8px 24px rgba(0, 0, 0, 0.08)',
        '--bm-color-on-accent': '#ffffff',
        '--bm-color-seat-active': 'var(--mantine-color-blue-6)',
        '--bm-color-seat-idle': 'var(--mantine-color-yellow-6)',
        '--bm-color-seat-unused': 'var(--mantine-color-zinc-3)',
        '--bm-color-success': 'var(--mantine-color-teal-6)',
        '--bm-color-success-soft': 'rgba(18, 184, 134, 0.10)',
        '--bm-color-success-border': 'rgba(18, 184, 134, 0.22)',
        '--bm-color-accent-neutral': 'var(--mantine-color-zinc-5)',
        '--bm-color-accent-neutral-soft': 'rgba(113, 113, 122, 0.10)',
        '--bm-color-accent-neutral-border': 'rgba(113, 113, 122, 0.22)',
        '--bm-color-accent-neutral-text': 'var(--mantine-color-zinc-5)',
        '--bm-color-danger': 'var(--mantine-color-red-6)',
        '--bm-color-danger-soft': 'rgba(224, 49, 49, 0.10)',
        '--bm-color-danger-border': 'rgba(224, 49, 49, 0.28)',
        '--bm-color-danger-text': 'var(--mantine-color-red-7)',
        '--bm-color-warning': 'var(--mantine-color-orange-6)',
        '--bm-color-caution': 'var(--mantine-color-yellow-7)',
        '--bm-color-caution-soft': 'rgba(245, 159, 0, 0.10)',
        '--bm-color-caution-border': 'rgba(245, 159, 0, 0.24)',
        '--bm-color-caution-text': 'var(--mantine-color-yellow-8)',
        ...vizAccentVars(),
    },
    light: modeVars(lightPalette),
    dark: modeVars(darkPalette),
});
